import gql from 'graphql-tag';
import apolloClient from '../apollo';

/**
 * State
 */
const getDefaultState = () => {
  return {
    offerPositionBoilerplates: [],
    offerPositionBoilerplateCount: 0,
  };
};
const state = getDefaultState();

const defaultOfferPositionBoilerplate = () => {
  return {
    _id: '_new',
    number: '_new',
    name: '',
    description: '',
    quantity: 1.0,
    price: 0.0,
    priceUnit: '',
    quantityUnit: '',
    offerBoilerplate: null,
  };
};

/**
 * Defines the fields which are requested for an offer position boilerplate
 * used for all queries/mutations which return an offer position boilerplate
 * @type {DocumentNode}
 */
const OFFER_POSITION_BOILERPLATE_REQUEST_FIELDS = gql`
  fragment offerPositionBoilerplateFields on OfferPositionBoilerplate {
    _id
    number
    name
    description
    quantity
    price
    priceUnit
    quantityUnit
    offerBoilerplate {
      _id
      title
    }
    createdAt
    updatedAt
  }
`;

/**
 * Create payload for offer position boilerplate mutations
 */
const createPayload = (offerPositionBoilerplate) => {
  const payload = {
    name: offerPositionBoilerplate.name,
    description: offerPositionBoilerplate.description,
    quantity: offerPositionBoilerplate.quantity,
    price: offerPositionBoilerplate.price,
    priceUnit: offerPositionBoilerplate.priceUnit,
    quantityUnit: offerPositionBoilerplate.quantityUnit,
    offerBoilerplate:
      offerPositionBoilerplate.offerBoilerplate?.id || offerPositionBoilerplate.offerBoilerplate,
  };

  return payload;
};

/**
 * Actions
 */
const actions = {
  async fetchOfferPositionBoilerplates({ commit, dispatch }, { pagination, sorting, filters }) {
    try {
      const { data, errors } = await apolloClient.query({
        query: gql`
          query offerPositionBoilerplatesPaginated(
            $pagination: PaginationInput
            $sorting: SortingInput
            $filters: OfferPositionBoilerplateFilterInput
          ) {
            offerPositionBoilerplatesPaginated(
              pagination: $pagination
              sorting: $sorting
              filters: $filters
            ) {
              offerPositionBoilerplates {
                ...offerPositionBoilerplateFields
              }
              offerPositionBoilerplateCount
            }
          }
          ${OFFER_POSITION_BOILERPLATE_REQUEST_FIELDS}
        `,
        variables: {
          pagination: pagination,
          sorting: sorting,
          filters: filters,
        },
      });

      console.log('fetchOfferPositionBoilerplates():', data.offerPositionBoilerplatesPaginated);

      commit(
        'SET_OFFER_POSITION_BOILERPLATES',
        data.offerPositionBoilerplatesPaginated.offerPositionBoilerplates
      );
      commit(
        'SET_OFFER_POSITION_BOILERPLATE_COUNT',
        data.offerPositionBoilerplatesPaginated.offerPositionBoilerplateCount
      );
    } catch (err) {
      throw new Error(err);
    }
  },

  async fetchOfferPositionBoilerplateByNumber({ commit, dispatch }, number) {
    try {
      const { data, errors } = await apolloClient.query({
        query: gql`
          query offerPositionBoilerplateByNumber($number: Int!) {
            offerPositionBoilerplateByNumber(number: $number) {
              ...offerPositionBoilerplateFields
            }
          }
          ${OFFER_POSITION_BOILERPLATE_REQUEST_FIELDS}
        `,
        variables: {
          number: parseInt(number),
        },
      });

      // console.log('fetchOfferPositionBoilerplateByNumber()', data.offerPositionBoilerplateByNumber);
      commit('ADD_OR_UPDATE_OFFER_POSITION_BOILERPLATE', data.offerPositionBoilerplateByNumber);
    } catch (err) {
      throw new Error(err);
    }
  },

  async createOfferPositionBoilerplate({ commit, dispatch }, offerPositionBoilerplate) {
    try {
      const payload = createPayload(offerPositionBoilerplate);

      const { data, errors } = await apolloClient.mutate({
        mutation: gql`
          mutation createOfferPositionBoilerplate($input: OfferPositionBoilerplateInput!) {
            createOfferPositionBoilerplate(input: $input) {
              ...offerPositionBoilerplateFields
            }
          }
          ${OFFER_POSITION_BOILERPLATE_REQUEST_FIELDS}
        `,
        variables: {
          input: payload,
        },
      });

      commit('ADD_OR_UPDATE_OFFER_POSITION_BOILERPLATE', data.createOfferPositionBoilerplate);
      return data.createOfferPositionBoilerplate;
    } catch (err) {
      throw new Error(err);
    }
  },

  async updateOfferPositionBoilerplate({ commit, dispatch }, offerPositionBoilerplate) {
    try {
      const payload = createPayload(offerPositionBoilerplate);

      const { data, errors } = await apolloClient.mutate({
        mutation: gql`
          mutation updateOfferPositionBoilerplate(
            $_id: ID!
            $input: OfferPositionBoilerplateInput!
          ) {
            updateOfferPositionBoilerplate(_id: $_id, input: $input) {
              ...offerPositionBoilerplateFields
            }
          }
          ${OFFER_POSITION_BOILERPLATE_REQUEST_FIELDS}
        `,
        variables: {
          _id: offerPositionBoilerplate._id,
          input: payload,
        },
      });

      commit('ADD_OR_UPDATE_OFFER_POSITION_BOILERPLATE', data.updateOfferPositionBoilerplate);
      return data.updateOfferPositionBoilerplate;
    } catch (err) {
      throw new Error(err);
    }
  },

  async initOfferPositionBoilerplate({ commit }) {
    commit('ADD_OR_UPDATE_OFFER_POSITION_BOILERPLATE', defaultOfferPositionBoilerplate());
  },

  localUpdate({ commit }, offerPositionBoilerplate) {
    commit('ADD_OR_UPDATE_OFFER_POSITION_BOILERPLATE', offerPositionBoilerplate);
  },

  offerPositionBoilerplateCleanup({ commit }) {
    commit('SET_DEFAULT_STATE');
  },
};

/**
 * Mutations
 */
const mutations = {
  SET_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_OFFER_POSITION_BOILERPLATE_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_OFFER_POSITION_BOILERPLATES: (state, offerPositionBoilerplates) =>
    (state.offerPositionBoilerplates = offerPositionBoilerplates),
  SET_OFFER_POSITION_BOILERPLATE_COUNT: (state, count) =>
    (state.offerPositionBoilerplateCount = count),
  ADD_OR_UPDATE_OFFER_POSITION_BOILERPLATE: (state, updatedOfferPositionBoilerplate) => {
    const existingOfferPositionBoilerplate = state.offerPositionBoilerplates.find(
      (offerPositionBoilerplate) =>
        offerPositionBoilerplate._id === updatedOfferPositionBoilerplate._id
    );
    if (existingOfferPositionBoilerplate) {
      Object.assign(existingOfferPositionBoilerplate, updatedOfferPositionBoilerplate);
    } else {
      state.offerPositionBoilerplates.push(updatedOfferPositionBoilerplate);
    }
    console.log('ADD_OR_UPDATE_OFFER_POSITION_BOILERPLATE()', state.offerPositionBoilerplates);
  },
};

/**
 * Getters
 */
const getters = {
  getOfferPositionBoilerplates: (state) => state.offerPositionBoilerplates,
  getOfferPositionBoilerplate: (state) => (offerPositionBoilerplateId) =>
    state.offerPositionBoilerplates.find(
      (offerPositionBoilerplate) => offerPositionBoilerplate.id === offerPositionBoilerplateId
    ),
  getOfferPositionBoilerplateByNumber: (state) => (number) =>
    state.offerPositionBoilerplates.find(
      (offerPositionBoilerplate) => offerPositionBoilerplate.number == number
    ),
  getOfferPositionBoilerplateCount: (state) => state.offerPositionBoilerplateCount,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
